import { MessageSquare, Zap } from 'lucide-react';

// TypeScript interfaces for pricing data
export interface Feature {
  id: string;
  displayName: string;
  category: string;
  note?: boolean;
  noteOnly?: boolean;
}

export interface TierFeatures {
  [key: string]: {
    included: boolean;
    value?: string;
    noteOnly?: boolean;
  };
}

export interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  icon: any;
  buttonText: string;
  buttonVariant: string;
  highlight: boolean;
  features: TierFeatures;
}

// Master list of all features - ordered so the Free card reads INCLUDED-first,
// NOT-INCLUDED-last (founder directive 2026-06). The Unlimited card mirrors this
// exact order for side-by-side visual parity (it's all ✓). This ordering
// SUPERSEDES the prior "match homepage section scroll order" convention.
//
// Free INCLUDED block (✓):  case_briefs → study_mode → notes → topic_outlines →
//   chat_messages → community → study_groups
// Free NOT-INCLUDED block (✗):  premium_flashcards → question_bank →
//   issue_spotter → essay_coach → outline_iq → video_courses
export const masterFeatures: Feature[] = [
  // --- Free: INCLUDED (✓) ---
  { id: 'case_briefs', displayName: 'Case briefs', category: 'Core Features' },
  { id: 'study_mode', displayName: 'Study Mode & Flashcard Engine', category: 'Study Tools' },
  // Carry-forward is inherent to having an account — the knowledge graph builds
  // from free study, so it's included for everyone (Free ✓ AND Unlimited ✓).
  // Guardrail-safe: "work carries forward" = knowledge & progress, NOT readiness scores.
  { id: 'carry_forward', displayName: 'Your work carries forward into bar prep', category: 'Study Tools' },
  { id: 'notes', displayName: 'Notes & templates', category: 'Study Tools' },
  { id: 'topic_outlines', displayName: 'Subject Outlines', category: 'Core Features' },
  { id: 'chat_messages', displayName: 'Ask JDS', category: 'Core Features' },
  { id: 'community', displayName: 'Community (coming soon)', category: 'Community' },
  { id: 'study_groups', displayName: 'Study Groups (coming soon)', category: 'Community' },
  // --- Free: NOT INCLUDED (✗) ---
  { id: 'premium_flashcards', displayName: 'Premium Flashcard Content', category: 'Study Tools' },
  { id: 'question_bank', displayName: 'Premium Practice Questions', category: 'Study Tools' },
  { id: 'issue_spotter', displayName: 'Issue Spotter', category: 'Practice Tools' },
  { id: 'essay_coach', displayName: 'Essay Coach', category: 'Practice Tools' },
  { id: 'outline_iq', displayName: 'Outline IQ feedback', category: 'Study Tools' },
  { id: 'video_courses', displayName: 'Video courses', category: 'Courses' },
];

// Pricing tiers configuration
export const pricingTiers: PricingTier[] = [
  {
    name: 'Free',
    price: '$0',
    period: 'month',
    description: 'A free tier you can actually study from, not a trial that expires. Start now, no card required.',
    icon: MessageSquare,
    buttonText: 'Current Plan',
    buttonVariant: 'outline',
    highlight: false,
    features: {
      // INCLUDED (✓)
      case_briefs: { included: true },
      study_mode: { included: true },
      carry_forward: { included: true },
      notes: { included: true },
      topic_outlines: { included: true, value: 'Limited' },
      chat_messages: { included: true, value: '30 per month' },
      community: { included: true },
      study_groups: { included: true },
      // NOT INCLUDED (✗)
      premium_flashcards: { included: false },
      question_bank: { included: false },
      issue_spotter: { included: false },
      essay_coach: { included: false },
      outline_iq: { included: false },
      video_courses: { included: false },
    },
  },
  {
    name: 'Unlimited',
    price: '$20',
    period: 'month',
    description: 'Full access to all features.',
    icon: Zap,
    buttonText: 'Upgrade to Unlimited',
    buttonVariant: 'default',
    highlight: true,
    features: {
      // Mirrors the Free card's feature order for side-by-side parity. All ✓.
      case_briefs: { included: true },
      study_mode: { included: true },
      carry_forward: { included: true },
      notes: { included: true },
      topic_outlines: { included: true, value: 'Full Access' },
      chat_messages: { included: true, value: 'Unlimited' },
      community: { included: true },
      study_groups: { included: true },
      premium_flashcards: { included: true },
      question_bank: { included: true },
      issue_spotter: { included: true },
      essay_coach: { included: true },
      outline_iq: { included: true },
      video_courses: { included: true },
    },
  },
];

// Helper functions
export const getTierFeatureInfo = (tier: PricingTier, featureId: string) => {
  return tier.features[featureId];
};

export const isFeatureIncluded = (tier: PricingTier, featureId: string): boolean => {
  return tier.features[featureId]?.included || false;
};

/**
 * Parse a DISPLAY price string into minor units (cents).
 *
 * `PricingTier.price` is typed `string` and always holds a formatted display
 * value — `'$0'`, `'$20'`, `'$200'` from the static tiers above, or
 * `formatted_price` from the pricing API via `useDynamicPricing`. It is never
 * a number.
 *
 * This exists because that fact was not obvious at the call site. Analytics
 * code in PricingPage read the price as
 * `typeof tier.price === 'string' ? 0 : tier.price` — a runtime type check
 * that looks defensive and is in fact ALWAYS TRUE, so every
 * `initiate_checkout` event reported a value of 0. Nothing errored; the number
 * was merely, permanently, wrong. Parse here rather than re-deriving that
 * check anywhere else.
 *
 * Returns MINOR units because that is what conversion APIs want (OpenAI Ads,
 * Stripe). Divide by 100 for a display-dollar consumer.
 *
 * Unparseable input returns 0 deliberately — a price we cannot read must not
 * become a plausible-looking revenue figure. Non-numeric tier labels
 * ('Custom', 'Free', 'Contact us') land here and are correct at 0.
 *
 * @example parsePriceToMinorUnits('$20')    // 2000
 * @example parsePriceToMinorUnits('$1,200') // 120000
 * @example parsePriceToMinorUnits('Custom') //      0
 */
export function parsePriceToMinorUnits(price: string | number | null | undefined): number {
  if (typeof price === 'number') {
    return Number.isFinite(price) ? Math.max(0, Math.round(price * 100)) : 0;
  }
  if (typeof price !== 'string') return 0;

  // Strip currency symbols, thousands separators, whitespace, and any
  // period suffix ('/mo', '/year').
  const cleaned = price.replace(/[^0-9.]/g, '');
  if (!cleaned) return 0;

  const value = parseFloat(cleaned);
  if (!Number.isFinite(value)) return 0;

  return Math.max(0, Math.round(value * 100));
} 